// Pure, client-side link-quality checker for the editor (FE-1136).
// No network calls — HTTP reachability is a separate ticket.
//
// `classifyLink` layers the v1-specific rules (empty / placeholder) on top of
// the canonical `validateUrl` from helpers.js, which is the same validator the
// settings fields use. Reusing it guarantees the Link Issues panel never
// disagrees with the inline field validation.

import { BLOCK_TYPES } from '@client/components/Block/constants'
import { SOCIAL_SERVICE_NAMES } from '@client/components/blocks/SocialV2/constants'
import { validateUrl } from './helpers'

export const LINK_RULES = {
  EMPTY: 'EMPTY',
  PLACEHOLDER: 'PLACEHOLDER',
  OUTLOOK_SAFELINK: 'OUTLOOK_SAFELINK',
  WHITESPACE: 'WHITESPACE',
  BROKEN_MAILTO: 'BROKEN_MAILTO',
  MISSING_PROTOCOL: 'MISSING_PROTOCOL',
  MALFORMED: 'MALFORMED',
}

// Substrings / values that signal an unfinished link.
const PLACEHOLDER_PATTERNS = [
  /\bexample\.(com|org)\b/i,
  /lorem/i,
  /your-link-here/i,
]

const isPlaceholder = (value) => {
  if (value === '#' || value.toLowerCase() === 'about:blank') return true
  return PLACEHOLDER_PATTERNS.some((re) => re.test(value))
}

// Outlook "Safe Links" protection wrapper. When users copy a link out of
// Outlook they often paste this redirect instead of the real destination — the
// URL is structurally valid but tenant-bound to the original recipient, so it
// only works for them and breaks (or leaks who clicked) for every other reader.
// It's always the wrong thing to put in a newsletter, hence its own clear rule.
// Matches every regional host (nam12., eur01., gbr01., …).
const isOutlookSafelink = (value) =>
  /safelinks\.protection\.outlook\.com/i.test(value)

// `www.foo.com`, `foo.com/bar`, protocol-relative `//foo.com` — a host that is
// missing its scheme rather than being outright garbage.
const looksLikeMissingProtocol = (value) =>
  /^\/\//.test(value) ||
  /^www\./i.test(value) ||
  /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}([/:?#].*)?$/i.test(value)

// Returns a LINK_RULES key when the value is a problem, or null when it's fine.
// `allowEmpty` lets callers (e.g. optional image/product links) treat a blank
// value as "no link" rather than an issue.
export const classifyLink = (value, { allowEmpty = false } = {}) => {
  const raw = value == null ? '' : String(value)
  const trimmed = raw.trim()

  if (trimmed === '') return allowEmpty ? null : LINK_RULES.EMPTY

  // Placeholder wins even when the value is a structurally valid URL
  // (e.g. `http://example.com` passes validateUrl but is clearly a stand-in).
  if (isPlaceholder(trimmed)) return LINK_RULES.PLACEHOLDER

  // Same idea for Outlook Safe Links: a valid-looking URL that's still always
  // wrong, so it has to be caught before validateUrl would accept it.
  if (isOutlookSafelink(trimmed)) return LINK_RULES.OUTLOOK_SAFELINK

  // validateUrl is the authority for everything else — anything it accepts
  // (real URLs, valid mailto:/tel:, allowed ##merge_tags##) is not an issue.
  if (validateUrl(raw)) return null

  // From here the value is genuinely invalid; pick the most actionable label.
  // Test the raw value, not `trimmed`: a stray leading/trailing space (the most
  // common copy-paste error) is exactly what this rule should catch, but it's
  // already been stripped from `trimmed`.
  if (/\s/.test(raw)) return LINK_RULES.WHITESPACE
  if (/^mailto:/i.test(trimmed)) return LINK_RULES.BROKEN_MAILTO
  if (looksLikeMissingProtocol(trimmed)) return LINK_RULES.MISSING_PROTOCOL
  return LINK_RULES.MALFORMED
}

// i18n keys reused from the block palette titles.
const BLOCK_LABEL_KEYS = {
  [BLOCK_TYPES.TEXT]: 'block.text.title',
  [BLOCK_TYPES.IMAGE]: 'block.image.title',
  [BLOCK_TYPES.BUTTON]: 'block.button.title',
  [BLOCK_TYPES.SIMPLE_BUTTON]: 'block.button.title',
  [BLOCK_TYPES.CONFIRM_BUTTON]: 'block.confirm-button.title',
  [BLOCK_TYPES.VIDEO]: 'block.video.title',
  [BLOCK_TYPES.PRODUCT]: 'block.product.title',
  [BLOCK_TYPES.HTML]: 'block.code.title',
  [BLOCK_TYPES.SHARE]: 'block.social-share.title',
  [BLOCK_TYPES.SHARE_V2]: 'block.social-share.title',
  [BLOCK_TYPES.FOLLOW]: 'block.social-follow.title',
  [BLOCK_TYPES.FOLLOW_V2]: 'block.social-follow.title',
}

const SOCIAL_TYPES = [
  BLOCK_TYPES.SHARE,
  BLOCK_TYPES.SHARE_V2,
  BLOCK_TYPES.FOLLOW,
  BLOCK_TYPES.FOLLOW_V2,
]

// Pull every authored href out of a parsed document. Uses getAttribute('href')
// so relative/anchor/mailto values stay as the author wrote them (the `.href`
// getter would resolve them against the document base). Only <a> elements that
// actually carry an href attribute are returned, so named anchors (<a name>)
// are ignored, while an explicit empty `href=""` is reported.
const linksFromDoc = (doc) =>
  Array.from(doc.querySelectorAll('a[href]')).map((el) => ({
    value: el.getAttribute('href') || '',
    anchorText: (el.textContent || '').trim(),
  }))

// First non-empty piece of visible text in an HTML fragment, used to label a
// product link by the product's name (e.g. "Best product"). Walks text nodes
// so it's robust to whatever wrapping markup the product uses; returns '' when
// there's no text. Capped to keep the label sane — the panel also truncates.
const firstTextLineFromDoc = (doc) => {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    const text = (node.textContent || '').trim()
    if (text) return text.slice(0, 80)
    node = walker.nextNode()
  }
  return ''
}

// Parsing HTML with DOMParser is the only expensive part of extraction, and the
// `linkIssues` computed re-runs on every edit (the always-visible header badge
// keeps it permanently observed). Cache the parse per block, keyed on the source
// string: editing one block re-parses only that block, while unchanged blocks
// return their cached result — so cost no longer scales with document size on
// the hot editing path. The WeakMap keys on the (stable) block instance, so
// entries are GC'd with the block; no eviction logic and no leak. One parse
// yields both the anchors and the product label, so Product no longer parses
// twice. MobX reactivity is unaffected — extractLinks still reads
// block.htmlText/content, so the source strings remain tracked dependencies.
const EMPTY_PARSE = { links: [], firstTextLine: '' }
const htmlParseCache = new WeakMap()

const parseBlockHtml = (block, html) => {
  if (!html) return EMPTY_PARSE
  const src = String(html)
  const cached = htmlParseCache.get(block)
  if (cached && cached.src === src) return cached.parsed

  const doc = new DOMParser().parseFromString(src, 'text/html')
  const parsed = {
    links: linksFromDoc(doc),
    firstTextLine: firstTextLineFromDoc(doc),
  }
  htmlParseCache.set(block, { src, parsed })
  return parsed
}

// Returns the links to check for a block:
//   [{ value, labelKey, anchorText, allowEmpty }]
// `allowEmpty` is true only for genuinely optional links (image/product wrap
// links), where a blank value just means "not a link" and shouldn't be flagged.
export const extractLinks = (block) => {
  if (!block || !block.type) return []
  const labelKey = BLOCK_LABEL_KEYS[block.type]
  const values = block.settings ? block.settings.values || {} : {}

  switch (block.type) {
    // All three expose a single url field. Confirm button defaults to the
    // ##confirmation_link## merge tag (which validates) — only flagged if the
    // author clears or breaks it.
    case BLOCK_TYPES.BUTTON:
    case BLOCK_TYPES.SIMPLE_BUTTON:
    case BLOCK_TYPES.CONFIRM_BUTTON:
      return [
        {
          value: values.url || '',
          labelKey,
          anchorText: values.text || '',
          allowEmpty: false,
        },
      ]

    case BLOCK_TYPES.IMAGE:
      // An empty image link just means the image isn't clickable — not an issue.
      if (!values.imageLink) return []
      return [
        {
          value: values.imageLink,
          labelKey,
          anchorText: values.altText || '',
          allowEmpty: true,
        },
      ]

    case BLOCK_TYPES.VIDEO:
      // Empty videoUrl just means the thumbnail isn't clickable — not an issue.
      if (!values.videoUrl) return []
      return [
        {
          value: values.videoUrl,
          labelKey,
          anchorText: values.altText || '',
          allowEmpty: true,
        },
      ]

    case BLOCK_TYPES.TEXT:
      return parseBlockHtml(block, block.htmlText).links.map((link) => ({
        ...link,
        labelKey,
        allowEmpty: false,
      }))

    case BLOCK_TYPES.PRODUCT: {
      // Product has both rich-text anchors and an optional image wrap-link.
      const parsed = parseBlockHtml(block, block.htmlText)
      const links = parsed.links.map((link) => ({
        ...link,
        labelKey,
        allowEmpty: false,
      }))
      // The wrap-link is optional, same as Image: a blank value just means the
      // product image isn't clickable — a layout the renderer explicitly
      // supports (Product/index.jsx, the `!imageLink` branch) and the default
      // state of a fresh product. So only check it when present; a
      // present-but-invalid link is still flagged.
      if (values.imageLink) {
        links.push({
          value: values.imageLink,
          labelKey,
          anchorText: parsed.firstTextLine,
          allowEmpty: true,
        })
      }
      return links
    }

    case BLOCK_TYPES.HTML:
      return parseBlockHtml(block, block.content).links.map((link) => ({
        ...link,
        labelKey,
        allowEmpty: false,
      }))

    // Only Follow blocks expose an author-editable profile URL (service.link).
    // Share blocks auto-generate their href from SOCIAL_SERVICE_SHARE_LINKS and
    // leave service.link empty (there's no URL field for them — see
    // EditSocialService), so checking it would surface unresolvable
    // "Empty link" issues for every share service. The generated share URLs are
    // hardcoded constants, so there's nothing for the author to fix.
    case BLOCK_TYPES.FOLLOW:
    case BLOCK_TYPES.FOLLOW_V2: {
      // filteredServices is the computed that returns only the services shown
      // for this block type; legacy Social blocks fall back to `services`.
      const services = block.filteredServices || block.services || []
      return services.map((service) => ({
        value: service.link || '',
        labelKey,
        // Social service names are i18n keys; the panel translates anchorTextKey
        // and falls back to the raw name.
        anchorText: service.name || '',
        anchorTextKey: SOCIAL_SERVICE_NAMES[service.name] || '',
        allowEmpty: false,
      }))
    }

    default:
      // Link-less blocks (SPACING, DIVIDER, TIMER, FORM, …) and blocks whose
      // link is auto-generated (BANNER) have nothing to check.
      return []
  }
}

export const LINK_BLOCK_TYPES = Object.keys(BLOCK_LABEL_KEYS)

export { SOCIAL_TYPES }
